import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { menuData as staticMenuData, categoryNames as staticCategoryNames } from '@/data/menuData'
import { fetchMenu, transformMenuData, cacheMenu, getCachedMenu } from '@/services/api'

export const useMenuStore = defineStore('menu', () => {
  // State
  const items = ref(staticMenuData) // Fallback to static data
  const categories = ref(staticCategoryNames)
  const categoryMeta = ref({})
  const loading = ref(false)
  const error = ref(null)
  const currentRestaurantSlug = ref(null)
  const menuSource = ref('static') // 'static' | 'api'
  const ageVerified = ref(false) // Session-level age verification for alcohol

  // Getters
  const getAllCategories = computed(() => {
    // Map categories and filter out empty ones
    return Object.keys(categories.value)
      .map(key => {
        const meta = categoryMeta.value[key] || {}
        return {
          ...meta, // Spread meta first
          id: key, // Then override id with the correct key (e.g., 'cat_714')
          name: categories.value[key],
          itemCount: (items.value[key] || []).length,
          laravelId: meta.id // Keep original Laravel ID for reference
        }
      })
      .filter(cat => cat.itemCount > 0) // Only show categories with items
  })

  const getItemsByCategory = computed(() => (categoryId) => {
    return items.value[categoryId] || []
  })

  const getItemById = computed(() => (itemId) => {
    for (const category of Object.values(items.value)) {
      const item = category.find(i => i.id === itemId)
      if (item) return item
    }
    return null
  })

  const isLoading = computed(() => loading.value)
  const hasError = computed(() => !!error.value)
  
  // Check if a category is marked as alcohol
  const isAlcoholCategory = computed(() => (categoryId) => {
    return categoryMeta.value[categoryId]?.alcohol === 1
  })
  const isFromApi = computed(() => menuSource.value === 'api')

  // Actions
  
  /**
   * Load menu from Laravel API for a specific restaurant
   * Falls back to static data if API fails
   */
  async function loadMenu(restaurantSlug) {
    // Skip if already loaded for this restaurant
    if (currentRestaurantSlug.value === restaurantSlug && menuSource.value === 'api') {
      return
    }

    loading.value = true
    error.value = null

    try {
      // Check cache first
      const cached = getCachedMenu(restaurantSlug)
      if (cached) {
        applyMenuData(cached, restaurantSlug, 'api')
        loading.value = false
        
        // Refresh in background
        refreshMenuInBackground(restaurantSlug)
        return
      }

      // Fetch from API
      const apiResponse = await fetchMenu(restaurantSlug)
      const transformed = transformMenuData(apiResponse)
      
      // Cache the result
      cacheMenu(restaurantSlug, transformed)
      
      // Apply to store
      applyMenuData(transformed, restaurantSlug, 'api')
      
    } catch (err) {
      console.error('Failed to load menu from API:', err)
      error.value = err.message
      
      // Fall back to static data for demo purposes
      console.warn('Falling back to static menu data')
      applyMenuData({
        items: staticMenuData,
        categories: staticCategoryNames,
        categoryMeta: {}
      }, restaurantSlug, 'static')
    } finally {
      loading.value = false
    }
  }

  /**
   * Apply menu data to store state
   */
  function applyMenuData(data, slug, source) {
    items.value = data.items
    categories.value = data.categories
    categoryMeta.value = data.categoryMeta || {}
    currentRestaurantSlug.value = slug
    menuSource.value = source
  }

  /**
   * Refresh menu in background without blocking UI
   */
  async function refreshMenuInBackground(restaurantSlug) {
    try {
      const apiResponse = await fetchMenu(restaurantSlug)
      const transformed = transformMenuData(apiResponse)
      cacheMenu(restaurantSlug, transformed)
      
      // Only update if still on the same restaurant
      if (currentRestaurantSlug.value === restaurantSlug) {
        applyMenuData(transformed, restaurantSlug, 'api')
      }
    } catch (err) {
      // Silent fail for background refresh
      console.warn('Background menu refresh failed:', err.message)
    }
  }

  /**
   * Reset to static menu data (for testing/demo)
   */
  function useStaticMenu() {
    items.value = staticMenuData
    categories.value = staticCategoryNames
    categoryMeta.value = {}
    currentRestaurantSlug.value = null
    menuSource.value = 'static'
    error.value = null
  }

  /**
   * Set age verification status for this session
   */
  function setAgeVerified(verified) {
    ageVerified.value = verified
  }

  /**
   * Clear any errors
   */
  function clearError() {
    error.value = null
  }

  return {
    // State
    items,
    categories,
    categoryMeta,
    loading,
    error,
    currentRestaurantSlug,
    menuSource,
    ageVerified,
    
    // Getters
    getAllCategories,
    getItemsByCategory,
    getItemById,
    isLoading,
    hasError,
    isFromApi,
    isAlcoholCategory,
    
    // Actions
    loadMenu,
    useStaticMenu,
    setAgeVerified,
    clearError
  }
})
